/* eslint-disable prettier/prettier */
import { useState, useEffect } from 'react';
import { Save, Undo2, X, Loader2 } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { FileService } from '../../services/file.service';
import { TreatyEntityType } from '../../types/TreatyEntityType';
import { CustomSelect } from './Select';
import { CustomMultiSelect } from './MultiSelect';
import {
  format,
  parseISO,
  addYears,
  isValid,
  differenceInCalendarMonths,
  differenceInCalendarYears,
  differenceInDays,
} from 'date-fns';

export const KeyValuePair = ({
  label,
  value,
  aiValue,
  entityType,
  entityId,
  fieldKey,
  editable = true,
  refreshTreatyData,
  fieldConfig = {},
  extraData = {},
}: {
  label: string;
  value: string;
  aiValue?: string;
  entityType?: TreatyEntityType;
  entityId?: string;
  fieldKey?: string;
  editable?: boolean;
  refreshTreatyData?: () => void;
  fieldConfig?: {
    type?: 'text' | 'select' | 'multi-select' | 'percent' | 'date' | 'date-or-continuous';
    options?: string[];
    linkedField?: string;
    linkLogic?: (val: string) => string;
  };
  extraData?: Record<string, any>;
}) => {
  const [inputValue, setInputValue] = useState(value ?? '');
  const [savedValue, setSavedValue] = useState(value ?? '');
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [hasTriedToSave, setHasTriedToSave] = useState(false);

  const resolvedOptions =
    fieldConfig.options?.map((val: any) =>
      typeof val === 'string' ? { label: val, value: val } : val,
    ) ?? [];

  useEffect(() => {
    setSavedValue(value ?? '');
    setInputValue(value ?? '');
  }, [value]);

  useEffect(() => {
    setIsDirty(inputValue !== savedValue);
  }, [inputValue, savedValue]);

  const validationRules: Record<string, (val: string) => string | null> = {
    UMR: (val: string) => {
      if (!/^B[A-Z0-9]{16}$/.test(val)) {
        return 'UMR must start with B and contain 17 alphanumeric characters';
      }
      return null;
    },
    brokerageRate: (val: string) => {
      if (val === 'None') return null;
      if (!/^100$|^\d{1,2}(\.\d{1,2})?$/.test(val)) {
        return 'Must be a percentage between 0 and 100';
      }
      return null;
    },
    taxesRate: (val: string) => {
      if (val === 'None') return null;
      if (!/^100$|^\d{1,2}(\.\d{1,2})?$/.test(val)) {
        return 'Must be a percentage between 0 and 100';
      }
      return null;
    },
    premiumRate: (val: string) => {
      if (val === 'None') return null;
      if (!/^100$|^\d{1,2}(\.\d{1,2})?$/.test(val)) {
        return 'Must be a percentage between 0 and 100';
      }
      return null;
    },
    expiryDate: (val: string) => {
      if (!val || val === 'Continuous') return null;
      if (!isValid(parseISO(val))) return 'Invalid date';
      if (extraData?.linkedInceptionDate) {
        const inception = parseISO(extraData.linkedInceptionDate);
        const expiry = parseISO(val);
        if (isValid(inception) && expiry <= inception) {
          return 'Expiry date must be after inception date';
        }
      }
      return null;
    },
  };

  const handleSave = async () => {
    if (!entityType || !entityId || !fieldKey) return;

    setHasTriedToSave(true);

    try {
      setIsSaving(true);
      setValidationError(null);

      const validator = validationRules[fieldKey];
      if (validator) {
        const error = validator(inputValue);
        if (error) {
          setValidationError(error);
          setShake(true);
          setTimeout(() => setShake(false), 500);
          return;
        }
      }

      let newValue = inputValue;

      if (
        (fieldConfig.type === 'date' || fieldConfig.type === 'date-or-continuous') &&
        inputValue !== 'Continuous'
      ) {
        const parsed = parseISO(inputValue);
        if (isValid(parsed)) {
          newValue = format(parsed, 'MM/dd/yyyy'); // <-- Convert before saving
        }
      }

      await FileService.updateField({
        entity: entityType,
        entityId,
        field: fieldKey,
        newValue,
      });

      if (fieldConfig.linkedField && fieldConfig.linkLogic) {
        const linkedValue = fieldConfig.linkLogic(newValue);
        await FileService.updateField({
          entity: entityType,
          entityId,
          field: fieldConfig.linkedField,
          newValue: linkedValue,
        });
      }

      setSavedValue(newValue);
      setIsEditing(false);
      if (refreshTreatyData) refreshTreatyData();
    } catch {
      // silent
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setInputValue(savedValue);
    setIsEditing(false);
    setValidationError(null);
    setHasTriedToSave(false);
  };

  const handleResetToAI = async () => {
    if (!entityType || !entityId || !fieldKey) return;
    try {
      setIsResetting(true);
      const res = await FileService.resetField({
        entity: entityType,
        entityId,
        field: fieldKey,
      });
      const ai = res.data.ai_result;
      setInputValue(ai);
      setSavedValue(ai);
      setIsEditing(false);
      setValidationError(null);
      if (refreshTreatyData) refreshTreatyData();
    } catch {
      // silent
    } finally {
      setIsResetting(false);
    }
  };

  const TooltipIcon = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Tooltip.Provider>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="px-2 py-1 text-xs text-white bg-black rounded shadow"
            side="top"
            sideOffset={5}
          >
            {label}
            <Tooltip.Arrow className="fill-black" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );

  const shouldShowResetToAI = inputValue !== aiValue;

  return (
    <div className="flex items-center justify-between py-1 group">
      <div className="flex items-center w-1/3 gap-1">
        <span
          className={`font-medium text-md ${validationError ? 'text-red-600' : 'text-primary'}`}
        >
          {label}
        </span>
      </div>

      <div className="flex items-center justify-end w-2/3 gap-2">
        {editable ? (
          <>
            {isEditing ? (
              <>
                <div className="relative w-full">
                  {fieldConfig?.type === 'percent' ? (
                    <div className="relative flex items-center w-full gap-2">
                      {inputValue === 'None' ? (
                        <span className="text-sm text-muted-foreground"></span>
                      ) : (
                        <div className="relative flex items-center w-full">
                          {hasTriedToSave && validationError && (
                            <div className="absolute inset-y-0 z-10 flex items-center left-1">
                              <TooltipIcon label={validationError}>
                                <span className="flex items-center justify-center w-4 h-4 text-xs text-red-600 bg-white border border-red-600 rounded-full cursor-pointer">
                                  ?
                                </span>
                              </TooltipIcon>
                            </div>
                          )}
                          <input
                            type="text"
                            className={`w-full px-6 py-1 pr-6 text-right border ${
                              hasTriedToSave && validationError
                                ? 'border-red-500'
                                : 'border-gray-300'
                            } rounded focus:outline-primary text-muted-foreground font-mono ${shake ? 'animate-shake' : ''}`}
                            style={{
                              paddingLeft: hasTriedToSave && validationError ? '2rem' : '0.5rem',
                            }}
                            value={inputValue}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9.]/g, '');
                              setInputValue(val);
                            }}
                            disabled={isSaving || isResetting}
                            autoFocus
                          />
                          <span className="absolute inset-y-0 flex items-center text-sm text-gray-400 pointer-events-none right-2">
                            %
                          </span>
                        </div>
                      )}

                      <label className="flex items-center gap-1 text-sm text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={inputValue === 'None'}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setInputValue('None');
                              setValidationError(null);
                            } else {
                              setInputValue(savedValue !== 'None' ? savedValue : '');
                            }
                          }}
                          disabled={isSaving || isResetting}
                        />
                        None
                      </label>
                    </div>
                  ) : fieldConfig?.type === 'date' ? (
                    <input
                      type="date"
                      className="w-full px-2 py-1 border rounded"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      disabled={isSaving || isResetting}
                      lang="en-US"
                    />
                  ) : fieldConfig?.type === 'date-or-continuous' ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        className="w-full px-2 py-1 border rounded"
                        value={inputValue !== 'Continuous' ? inputValue : ''}
                        onChange={(e) => setInputValue(e.target.value)}
                        disabled={inputValue === 'Continuous' || isSaving || isResetting}
                        lang="en-US"
                      />
                      <label className="flex items-center gap-1 text-sm text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={inputValue === 'Continuous'}
                          onChange={(e) => setInputValue(e.target.checked ? 'Continuous' : '')}
                          disabled={isSaving || isResetting}
                        />
                        Continuous
                      </label>
                    </div>
                  ) : fieldConfig?.type === 'select' && fieldConfig.options ? (
                    <CustomSelect
                      data={resolvedOptions}
                      value={inputValue}
                      onChange={(val) => setInputValue(val)}
                      disabled={isSaving || isResetting}
                    />
                  ) : fieldConfig?.type === 'multi-select' ? (
                    <CustomMultiSelect
                      value={(inputValue || '')
                        .split(',')
                        .map((v) => v.trim())
                        .filter(Boolean)
                        .map((v) => ({ label: v, value: v }))}
                      onChange={(selected) =>
                        setInputValue(selected.map((o) => o.value).join(', '))
                      }
                      options={resolvedOptions}
                    />
                  ) : (
                    <div className="relative flex items-center w-full">
                      {hasTriedToSave && validationError && (
                        <div className="absolute inset-y-0 z-10 flex items-center left-1">
                          <TooltipIcon label={validationError}>
                            <span className="flex items-center justify-center w-4 h-4 text-xs text-red-600 bg-white border border-red-600 rounded-full cursor-pointer">
                              ?
                            </span>
                          </TooltipIcon>
                        </div>
                      )}
                      <input
                        type="text"
                        className={`w-full pl-6 py-1  pr-2 text-right border ${
                          hasTriedToSave && validationError ? 'border-red-500' : 'border-gray-300'
                        } rounded focus:outline-primary text-muted-foreground font-mono ${shake ? 'animate-shake' : ''}`}
                        style={{
                          paddingLeft: hasTriedToSave && validationError ? '2rem' : '0.5rem',
                        }}
                        value={inputValue}
                        onChange={(e) => {
                          let val = e.target.value;
                          if (fieldKey === 'UMR') {
                            val = val.toUpperCase().replace(/[^A-Z0-9]/g, '');
                          }
                          setInputValue(val);
                        }}
                        maxLength={fieldKey === 'UMR' ? 17 : undefined}
                        disabled={isSaving || isResetting}
                        autoFocus
                      />
                    </div>
                  )}
                </div>

                <TooltipIcon label="Cancel">
                  <button onClick={handleCancel} disabled={isSaving || isResetting}>
                    <X className="w-5 h-5 text-red-500" />
                  </button>
                </TooltipIcon>

                {shouldShowResetToAI && (
                  <TooltipIcon label="Reset to AI value">
                    <button onClick={handleResetToAI} disabled={isResetting}>
                      {isResetting ? (
                        <Loader2 className="w-5 h-5 animate-spin text-primary" />
                      ) : (
                        <Undo2 className="w-5 h-5 text-primary" />
                      )}
                    </button>
                  </TooltipIcon>
                )}

                {isDirty && (
                  <TooltipIcon label="Save">
                    <button onClick={handleSave} disabled={isSaving}>
                      {isSaving ? (
                        <Loader2 className="w-5 h-5 animate-spin text-primary" />
                      ) : (
                        <Save className="w-5 h-5 text-primary" />
                      )}
                    </button>
                  </TooltipIcon>
                )}
              </>
            ) : (
              <span
                className={`w-full text-right cursor-pointer text-muted-foreground ${
                  !inputValue ? 'text-gray-400 italic' : ''
                }`}
                onDoubleClick={() => setIsEditing(true)}
                title="Double click to edit"
              >
                {inputValue
                  ? fieldConfig?.type === 'date' || fieldConfig?.type === 'date-or-continuous'
                    ? inputValue === 'Continuous'
                      ? 'Continuous'
                      : isValid(parseISO(inputValue))
                        ? format(parseISO(inputValue), 'MM/dd/yyyy')
                        : inputValue
                    : inputValue
                  : '-- click to edit --'}
              </span>
            )}
          </>
        ) : (
          <span className="w-full text-right text-muted-foreground">
            {inputValue
              ? fieldConfig?.type === 'date' || fieldConfig?.type === 'date-or-continuous'
                ? inputValue === 'Continuous'
                  ? 'Continuous'
                  : isValid(parseISO(inputValue))
                    ? format(parseISO(inputValue), 'MM/dd/yyyy')
                    : inputValue
                : inputValue
              : ''}
          </span>
        )}
      </div>
    </div>
  );
};
